import { PublicShell } from '@/components/layout/PublicShell';
import { useBranding, usePlatformName } from '@/hooks/useBranding';

export function AUPPage() {
  const name = usePlatformName();
  const { data } = useBranding();
  const supportEmail = data?.branding.support_email || 'bot@workercms.com';
  const today = new Date().toLocaleDateString('tr-TR');

  return (
    <PublicShell>
      <article className="container max-w-3xl py-12 space-y-6 text-sm leading-relaxed">
        <header className="space-y-1">
          <h1 className="text-3xl font-bold">Kabul Edilebilir Kullanım Politikası</h1>
          <p className="text-muted-foreground">Son güncelleme: {today}</p>
        </header>

        <p>
          Bu politika, {name} kullanılırken yasaklanan davranışları ve kullanım sınırlarını
          tanımlar. Politikaya aykırı kullanım, hesabınızın askıya alınmasına veya sonlandırılmasına
          yol açabilir.
        </p>

        <Section title="1. Yasaklı İçerik Üretimi">
          <p>Platform üzerinden aşağıdaki içerikleri üretemez, yayınlayamaz veya dağıtamazsınız:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Yasa dışı, çocuk istismarı içeren, cinsel içerikli veya müstehcen materyal.</li>
            <li>Şiddeti, nefreti, ırkçılığı veya ayrımcılığı teşvik eden içerikler.</li>
            <li>Telif hakkı, marka veya patent ihlali yapan içerikler.</li>
            <li>Yanıltıcı sağlık, tıbbi veya finansal tavsiye niteliğindeki içerikler.</li>
            <li>Dolandırıcılık, kimlik avı (phishing) veya kötü amaçlı yazılım dağıtan içerikler.</li>
            <li>Seçim manipülasyonu, siyasi deepfake veya dezenformasyon kampanyaları.</li>
            <li>Özel kişi ve kurumlara yönelik taciz, iftira veya tehdit içerikleri.</li>
            <li>Yapay zeka modellerinin politika filtrelerini aşmaya yönelik içerikler.</li>
          </ul>
        </Section>

        <Section title="2. Spam ve Otomasyon Suistimali">
          <ul className="list-disc pl-5 space-y-1">
            <li>Düşük kaliteli "content farming" amaçlı toplu üretim (AdSense spam).</li>
            <li>Aynı içeriğin farklı sitelere spam olarak dağıtılması.</li>
            <li>Arama motorlarını yanıltmaya yönelik black-hat SEO teknikleri.</li>
            <li>Sistemin teknik sınırlarını aşmaya yönelik otomatik istekler.</li>
          </ul>
        </Section>

        <Section title="3. Güvenlik ve Altyapı">
          <ul className="list-disc pl-5 space-y-1">
            <li>Platformu, diğer kullanıcıları veya üçüncü taraf sistemleri zarara uğratma girişimi.</li>
            <li>API anahtarlarınızı veya hesap erişiminizi başkalarıyla paylaşmak yasaktır.</li>
            <li>Sistemde güvenlik açığı keşfederseniz <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>{supportEmail}</a> adresine bildirin.</li>
            <li>Penetrasyon testi veya otomatik güvenlik taraması için önceden yazılı izin gereklidir.</li>
          </ul>
        </Section>

        <Section title="4. Kullanım Limitleri ve Adil Kullanım">
          <p>
            Hesaplar, kredi bakiyesine dayalı çalışır. Ancak altyapıyı korumak için aşağıdaki
            teknik limitler uygulanır:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Rate limit aşımı (dakika/saat bazında) geçici engellemeye neden olabilir.</li>
            <li>Aşırı paralel istek, API anahtarının geçici olarak durdurulmasına yol açabilir.</li>
            <li>Aynı IP'den çoklu hesap açma denemeleri suistimal sayılır.</li>
          </ul>
        </Section>

        <Section title="5. Yapay Zeka Çıktılarının Sorumluluğu">
          <p>
            Platform, yapay zeka modellerinin ürettiği içeriğin doğruluğunu garanti etmez. Üretilen
            içeriğin yayınlanmadan önce insan denetiminden geçirilmesi, gerçek bilgilerin
            doğrulanması ve yasal değerlendirmenin yapılması sizin sorumluluğunuzdadır.
          </p>
        </Section>

        <Section title="6. Moderasyon ve Yaptırımlar">
          <p>İhlal halinde {name} aşağıdaki yaptırımları uygulayabilir:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Uyarı ve içerik silme.</li>
            <li>Belirli özelliklere geçici erişim engeli.</li>
            <li>Hesabın askıya alınması (kredi iadesi yapılmaz).</li>
            <li>Hesabın kalıcı olarak kapatılması.</li>
            <li>Ağır yasa dışı durumlarda yetkili makamlara bildirim.</li>
          </ul>
        </Section>

        <Section title="7. Bildirim">
          <p>
            Bu politikayı ihlal eden içerik veya davranış fark ettiyseniz lütfen{' '}
            <a className="text-primary hover:underline" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>{' '}
            adresinden bize bildirin.
          </p>
        </Section>
      </article>
    </PublicShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}
